import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { addRecentSearch } from '../utils/recentSearches';
import { trackMetaSearch } from '../utils/analytics';

/**
 * Shared search + live-suggestions logic used by both the mobile SidePanel
 * search and the desktop persistent search bar, so both stay in sync with
 * a single implementation of the debounced fetch / navigation behaviour.
 */
export function useProductSearch(searchQuery, { onNavigate } = {}) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSuggestions = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      try {
        const res = await api.get('/api/products/search-suggestions', {
          params: { query: searchQuery }
        });
        if (cancelled) return;
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions(true);
      } catch (e) {
        if (!cancelled) console.error('SUGGESTION_ERROR:', e);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 280);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [searchQuery]);

  const finishNavigation = () => {
    setShowSuggestions(false);
    if (onNavigate) onNavigate();
  };

  const handleSearch = (customQuery = null) => {
    const finalQuery = customQuery || searchQuery;
    if (finalQuery && finalQuery.trim()) {
      const query = finalQuery.trim();
      addRecentSearch(query);
      trackMetaSearch(query);
      navigate(`/all-brands?search=${encodeURIComponent(query)}`);
      finishNavigation();
    }
  };

  const handleSmartSearch = (suggestion) => {
    addRecentSearch(suggestion.label);
    trackMetaSearch(suggestion.label, suggestion.category ? { content_category: suggestion.category } : {});
    if (suggestion.type === 'smart') {
      const params = new URLSearchParams();
      if (suggestion.gender) params.set('gender', suggestion.gender);
      if (suggestion.category) params.set('category', suggestion.category);
      if (suggestion.brand) params.set('brand', suggestion.brand);
      if (suggestion.colors || suggestion.colorFamily) {
        params.set('colors', suggestion.colors || suggestion.colorFamily);
      }
      if (suggestion.discount) params.set('discountRanges', suggestion.discount);
      if (suggestion.maxPrice) params.set('maxPrice', String(suggestion.maxPrice));
      navigate(`/all-brands?${params.toString()}`);
    } else if (suggestion.type === 'brand') {
      navigate(`/all-brands?brand=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'partner') {
      navigate(`/featured?brand=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'category') {
      navigate(`/all-brands?category=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'retailer') {
      navigate(`/all-brands?retailers=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'product') {
      navigate(`/product/${suggestion.id}`);
    } else {
      navigate(`/all-brands?search=${encodeURIComponent(suggestion.label)}`);
    }
    finishNavigation();
  };

  return {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSearch,
    handleSmartSearch
  };
}
